import { useState } from 'react'
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { CheckCircle, Error, HourglassEmpty, Refresh, Science, Warning } from '@mui/icons-material'
import ChunkerButtonWithLoading from 'components/ChunkerButtonWithLoading'
import OutlinedContainer from 'components/OutlinedContainer'
import HeaderSmall from 'components/HeaderSmall'
import {
  CampaignConfig,
  CampaignDefaults,
  PPSOpportunitySolution,
  RunHistoryEntry,
} from 'lib/pps/types'
import { RunStats } from 'types/OpportunityConfig'

interface CampaignDashboardProps {
  opportunitySolution: PPSOpportunitySolution | null
  onSubmit: (config: CampaignConfig) => Promise<void>
  onRerun: (config: CampaignConfig) => Promise<void>
  onRefresh?: () => void
  isFirstRun: boolean
  loading?: boolean
  defaults?: CampaignDefaults
}

function getStatusColor(
  status: string,
): 'success' | 'warning' | 'error' | 'default' | 'primary' | 'secondary' | 'info' {
  switch (status) {
    case 'COMPLETE':
      return 'success'
    case 'IN_PROGRESS':
      return 'primary'
    case 'PENDING':
      return 'default'
    case 'FAILED':
      return 'error'
    case 'UNSUCCESSFUL':
      return 'warning'
    default:
      return 'default'
  }
}

function getStatusIcon(status: string) {
  switch (status) {
    case 'COMPLETE':
      return <CheckCircle fontSize="small" />
    case 'IN_PROGRESS':
    case 'PENDING':
      return <HourglassEmpty fontSize="small" />
    case 'FAILED':
      return <Error fontSize="small" />
    case 'UNSUCCESSFUL':
      return <Warning fontSize="small" />
    default:
      return null
  }
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-'
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return '-'
  }
}

function RunHistoryTable({ history }: { history: RunHistoryEntry[] }) {
  if (history.length === 0) return null

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Run</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Emails</TableCell>
            <TableCell>Started</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {history.map((run) => (
            <TableRow key={run.id}>
              <TableCell>#{run.runNumber}</TableCell>
              <TableCell>
                <Chip
                  icon={getStatusIcon(run.status) || undefined}
                  label={run.status}
                  size="small"
                  color={getStatusColor(run.status)}
                />
              </TableCell>
              <TableCell align="right">{run.emailsSent.toLocaleString()}</TableCell>
              <TableCell>{formatDate(run.createdAt)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

function CampaignStatsSection({ stats }: { stats: RunStats }) {
  const titlesWithCounts = Object.entries(stats.contactsByTitle || {})
    .filter(([title]) => title !== 'Unknown')
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)

  const companiesWithCounts = Object.entries(stats.contactsByCompany || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)

  // Fallback to topCompanies (without counts) for existing data
  const companiesToShow =
    companiesWithCounts.length > 0
      ? companiesWithCounts
      : (stats.topCompanies || [])
          .slice(0, 10)
          .map((name) => [name, null] as [string, number | null])

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle2" gutterBottom>
          Campaign Statistics
        </Typography>

        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
          <Chip label={`${stats.totalContacts} Contacts`} size="small" variant="outlined" />
          <Chip label={`${stats.totalCompanies} Companies`} size="small" variant="outlined" />
        </Stack>

        {titlesWithCounts.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Titles
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {titlesWithCounts.map(([title, count]) => (
                <Chip
                  key={title}
                  label={`${title} ${count}`}
                  size="small"
                  variant="outlined"
                  sx={{ mb: 0.5 }}
                />
              ))}
            </Stack>
          </Box>
        )}

        {companiesToShow.length > 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Companies
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {companiesToShow.map(([company, count]) => (
                <Chip
                  key={company}
                  label={count !== null ? `${company} ${count}` : company}
                  size="small"
                  variant="outlined"
                  sx={{ mb: 0.5 }}
                />
              ))}
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

function CampaignDashboard({
  opportunitySolution,
  onSubmit,
  onRerun,
  onRefresh,
  isFirstRun,
  loading: externalLoading,
  defaults,
}: CampaignDashboardProps) {
  const [targetEmailCount, setTargetEmailCount] = useState<number | undefined>(
    opportunitySolution?.targetEmailCount || undefined,
  )
  const [maxRetry, setMaxRetry] = useState<number>(opportunitySolution?.maxRetry || 2)
  const [jobTitleAgent, setJobTitleAgent] = useState<boolean>(
    opportunitySolution?.aiConfig?.jobTitleAgent ?? false,
  )
  const [apolloOrgSearchAgent, setApolloOrgSearchAgent] = useState<boolean>(
    opportunitySolution?.aiConfig?.apolloOrgSearchAgent ?? false,
  )
  const [testMode, setTestMode] = useState<boolean>(opportunitySolution?.testMode ?? false)
  const [cherreLocationLimit, setCherreLocationLimit] = useState<number | undefined>(
    opportunitySolution?.cherreLocationLimit || undefined,
  )
  const [maxApolloOrgs, setMaxApolloOrgs] = useState<number | undefined>(
    opportunitySolution?.maxApolloOrgs || undefined,
  )
  const [loading, setLoading] = useState(false)

  const isNonProd = process.env.REACT_APP_ENV !== 'prod'
  const cumulativeEmailsSent = opportunitySolution?.cumulativeEmailsSent || 0
  const targetCount = opportunitySolution?.targetEmailCount || targetEmailCount || 0
  const progressPercent =
    targetCount > 0 ? Math.min((cumulativeEmailsSent / targetCount) * 100, 100) : 0
  const runHistory = opportunitySolution?.runHistory || []

  // Extract defaults with fallbacks to satisfy ESLint strict checks
  const defaultCherreLimit = defaults?.cherreLocationLimit ?? 500
  const defaultApolloOrgs = defaults?.maxApolloOrgs ?? 100

  const isValidConfig =
    maxRetry >= 1 && maxRetry <= 10 && (!targetEmailCount || targetEmailCount > 0)
  const canRerun =
    opportunitySolution?.status === 'COMPLETE' ||
    opportunitySolution?.status === 'FAILED' ||
    opportunitySolution?.status === 'UNSUCCESSFUL'
  const isLoading = loading || externalLoading

  const handleAction = async () => {
    setLoading(true)
    try {
      const config: CampaignConfig = {
        targetEmailCount: targetEmailCount || undefined,
        maxRetry,
        aiConfig: { jobTitleAgent, apolloOrgSearchAgent },
        testMode,
        cherreLocationLimit: cherreLocationLimit || undefined,
        maxApolloOrgs: maxApolloOrgs || undefined,
      }
      if (isFirstRun) {
        await onSubmit(config)
      } else {
        await onRerun(config)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <OutlinedContainer>
      <Stack spacing={3}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <HeaderSmall>Campaign Dashboard</HeaderSmall>
          {onRefresh && (
            <Tooltip title="Refresh">
              <IconButton onClick={onRefresh} size="small" disabled={isLoading}>
                <Refresh />
              </IconButton>
            </Tooltip>
          )}
        </Stack>

        {/* Test Mode Alert Banner */}
        {isNonProd && testMode && (
          <Alert severity="info" icon={<Science />}>
            <strong>TEST MODE</strong> - Completes after one run
          </Alert>
        )}

        {/* Campaign Overview - only show if campaign exists */}
        {opportunitySolution && (
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Chip
                  icon={getStatusIcon(opportunitySolution.status) || undefined}
                  label={`Status: ${opportunitySolution.status}`}
                  color={getStatusColor(opportunitySolution.status)}
                />
                {opportunitySolution.testMode && (
                  <Chip
                    icon={<Science fontSize="small" />}
                    label="Test Mode"
                    size="small"
                    color="info"
                  />
                )}
                {(opportunitySolution.runHistory?.length ?? 0) > 1 && (
                  <Chip
                    label={`Run #${opportunitySolution.runHistory?.length ?? 1}`}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Stack>

              {/* Emails Stats */}
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 2,
                    px: 3,
                    bgcolor: 'grey.100',
                    border: 1,
                    borderColor: 'grey.300',
                    borderRadius: 2,
                    maxWidth: 200,
                  }}
                >
                  <Typography variant="h3" fontWeight="bold" color="text.primary">
                    {cumulativeEmailsSent.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Emails Successful
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    (Total across all runs)
                  </Typography>
                </Box>
                {(opportunitySolution.emailsSkipped ?? 0) > 0 && (
                  <Box
                    sx={{
                      textAlign: 'center',
                      py: 2,
                      px: 3,
                      bgcolor: 'grey.100',
                      border: 1,
                      borderColor: 'grey.400',
                      borderRadius: 2,
                      maxWidth: 200,
                    }}
                  >
                    <Typography variant="h3" fontWeight="bold" color="text.secondary">
                      {(opportunitySolution.emailsSkipped ?? 0).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Emails Skipped
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      (This run only)
                    </Typography>
                  </Box>
                )}
              </Stack>

              {/* Progress Bar - only show if target is set */}
              {opportunitySolution.targetEmailCount && opportunitySolution.targetEmailCount > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Target Progress
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {cumulativeEmailsSent.toLocaleString()} /{' '}
                      {opportunitySolution.targetEmailCount.toLocaleString()}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={progressPercent}
                    sx={{ height: 10, borderRadius: 5 }}
                    color={progressPercent >= 100 ? 'success' : 'primary'}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {progressPercent.toFixed(0)}% complete
                  </Typography>
                </Box>
              )}

              {/* Status Message */}
              {opportunitySolution.statusChangeMessage && (
                <Typography variant="body2" color="text.secondary">
                  {opportunitySolution.statusChangeMessage}
                </Typography>
              )}
            </CardContent>
          </Card>
        )}

        {/* Campaign Stats */}
        {opportunitySolution?.runStats && (
          <CampaignStatsSection stats={opportunitySolution.runStats} />
        )}

        {/* Run History */}
        {runHistory.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Run History
            </Typography>
            <RunHistoryTable history={runHistory} />
          </Box>
        )}

        {/* Campaign In Progress Message */}
        {opportunitySolution &&
          (opportunitySolution.status === 'IN_PROGRESS' ||
            opportunitySolution.status === 'PENDING') && (
            <Alert severity="info" icon={<HourglassEmpty />}>
              <strong>Campaign is running</strong> - Use the refresh button to check for updates.
            </Alert>
          )}

        {/* Campaign Settings Form - Hide when campaign is running */}
        {(isFirstRun || canRerun) && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Campaign Settings
            </Typography>

            <Stack spacing={2} sx={{ mt: 2 }}>
              <TextField
                label="Target Email Count"
                type="number"
                value={targetEmailCount || ''}
                onChange={(e) =>
                  setTargetEmailCount(e.target.value ? parseInt(e.target.value, 10) : undefined)
                }
                helperText="The number of emails you want to send. Leave blank to run once without a target."
                InputProps={{ inputProps: { min: 1 } }}
                fullWidth
                size="small"
              />

              <TextField
                label="Max Retries"
                type="number"
                value={maxRetry}
                onChange={(e) => setMaxRetry(parseInt(e.target.value, 10))}
                helperText={`Additional runs after the first attempt (1-10). Total runs = 1 + retries = ${
                  1 + maxRetry
                }.`}
                InputProps={{ inputProps: { min: 1, max: 10 } }}
                fullWidth
                size="small"
                error={maxRetry < 1 || maxRetry > 10}
              />

              <TextField
                label="Max Cherre Locations"
                type="number"
                value={cherreLocationLimit || ''}
                onChange={(e) =>
                  setCherreLocationLimit(e.target.value ? parseInt(e.target.value, 10) : undefined)
                }
                helperText={`Limit locations from Cherre. Leave blank for default (${defaultCherreLimit}).`}
                InputProps={{ inputProps: { min: 1, max: defaultCherreLimit } }}
                placeholder={String(defaultCherreLimit)}
                fullWidth
                size="small"
              />

              <TextField
                label="Max Apollo Organizations"
                type="number"
                value={maxApolloOrgs || ''}
                onChange={(e) =>
                  setMaxApolloOrgs(e.target.value ? parseInt(e.target.value, 10) : undefined)
                }
                helperText={`Limit orgs from Apollo search. Leave blank for default (${defaultApolloOrgs}).`}
                InputProps={{ inputProps: { min: 1, max: defaultApolloOrgs } }}
                placeholder={String(defaultApolloOrgs)}
                fullWidth
                size="small"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={jobTitleAgent}
                    onChange={(e) => setJobTitleAgent(e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">Smart Job Titles</Typography>
                    <Typography variant="caption" color="text.secondary">
                      AI analyzes each company to find the most relevant job titles.
                    </Typography>
                  </Box>
                }
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={apolloOrgSearchAgent}
                    onChange={(e) => setApolloOrgSearchAgent(e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">3PL Company Discovery</Typography>
                    <Typography variant="caption" color="text.secondary">
                      AI searches for 3PL and logistics companies directly in Apollo.
                    </Typography>
                  </Box>
                }
              />

              {/* Test Mode Toggle - only in non-prod */}
              {isNonProd && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={testMode}
                      onChange={(e) => setTestMode(e.target.checked)}
                      color="info"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">
                        <Science fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                        Test Mode
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Completes after one run (use limits above to control scope)
                      </Typography>
                    </Box>
                  }
                />
              )}
            </Stack>

            {/* Action Button */}
            <Box sx={{ mt: 2 }}>
              <ChunkerButtonWithLoading
                fullWidth
                variant="contained"
                onClick={() => {
                  handleAction().catch(console.error)
                }}
                disabled={!isValidConfig || isLoading}
                loading={isLoading}
              >
                {isFirstRun ? 'Submit Campaign' : 'Start New Campaign Run'}
              </ChunkerButtonWithLoading>
              {!isValidConfig && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ display: 'block', mt: 1, textAlign: 'center' }}
                >
                  Please fix the configuration errors above.
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Stack>
    </OutlinedContainer>
  )
}

export default CampaignDashboard
